<template>
  <div>
    <h5 class="mb-3 font-weight-bold text-dark d-none d-lg-block">🔑 API 权限管理</h5>

    <b-tabs v-model="activeTab" pills card class="admin-tabs">

      <!-- ═══════════════════════════════════════════════════════════════
           Tab 1: 用户权益管理
      ═══════════════════════════════════════════════════════════════ -->
      <b-tab title="用户权益" active>
        <!-- 用户搜索 -->
        <b-card class="mb-2 mb-md-3 shadow-sm admin-search-card">
          <div class="admin-search-wrapper">
            <b-form-input
              v-model="searchId"
              placeholder="输入用户 ID 或用户名"
              size="sm"
              class="search-input"
              @keyup.enter="searchUser"
            />
            <b-button variant="primary" size="sm" class="search-btn" @click="searchUser" :disabled="loadingUser">
              {{ loadingUser ? '搜索中...' : '查询' }}
            </b-button>
          </div>
        </b-card>

        <!-- 查询结果 -->
        <div v-if="targetUser">
          <!-- 用户基础信息 -->
          <b-card class="mb-3 shadow-sm">
            <template #header>
              <span class="font-weight-bold">👤 用户信息</span>
              <b-button size="sm" variant="outline-secondary" class="float-right" style="margin-top: -4px;" @click="clearDetails">关闭详情</b-button>
            </template>
            <b-row>
              <b-col sm="3"><span class="text-muted">ID：</span><b>{{ targetUser.user_id }}</b></b-col>
              <b-col sm="3"><span class="text-muted">用户名：</span><b>{{ targetUser.user_name }}</b></b-col>
              <b-col sm="3"><span class="text-muted">昵称：</span><b>{{ targetUser.user_nick || '-' }}</b></b-col>
              <b-col sm="3"><span class="text-muted">角色：</span><b-badge variant="info">{{ targetUser.roles }}</b-badge></b-col>
            </b-row>
          </b-card>

          <!-- 积分管理 -->
          <b-card class="mb-3 shadow-sm">
            <template #header>
              <span class="font-weight-bold">💰 积分余额</span>
              <span class="ml-2 badge badge-success" style="font-size: 1rem;">{{ userPoints }} 分</span>
            </template>
            <b-button-group size="sm">
              <b-button variant="success" @click="openPointsModal('add')">➕ 发放积分</b-button>
              <b-button variant="warning" @click="openPointsModal('sub')">➖ 扣除积分</b-button>
            </b-button-group>
          </b-card>

          <!-- 订阅管理 -->
          <b-card class="shadow-sm">
            <template #header>
              <span class="font-weight-bold">📋 有效订阅</span>
              <b-button size="sm" variant="primary" class="float-right" @click="openGrantModal">＋ 新增订阅</b-button>
            </template>
            <b-table
              :items="userSubscriptions"
              :fields="subFields"
              small
              hover
              responsive
              show-empty
              empty-text="暂无有效订阅"
            >
              <template #cell(endpoint)="row">
                <code v-if="row.item.endpoint === '*'" class="text-danger font-weight-bold" style="font-size: 0.95rem;">* (全部接口)</code>
                <code v-else class="text-primary font-weight-bold" style="font-size: 0.95rem;">
                  {{ getFriendlyEndpointName(row.item.endpoint) }}
                </code>
                <span v-if="row.item.endpoint !== '*'" class="small text-muted ml-2">({{ row.item.endpoint }})</span>
              </template>
              <template #cell(end_date)="row">
                <span :class="isExpiringSoon(row.item.end_date) ? 'text-warning' : ''">
                  {{ row.item.end_date }}
                </span>
              </template>
            </b-table>
          </b-card>
        </div>

        <b-alert v-if="!targetUser && !loadingUser" show variant="secondary" class="mt-3 d-none d-md-block">
          请输入上方用户 ID 或用户名点击“查询”，或者在下方列表中直接选择用户“查看权限”。
        </b-alert>

        <!-- 用户列表 -->
        <b-card class="shadow-sm mt-3">
          <template #header>
            <span class="font-weight-bold">👥 用户列表</span>
            <b-button size="sm" variant="outline-primary" class="float-right" style="margin-top:-4px;" @click="fetchUserList" :disabled="loadingList">
              {{ loadingList ? '加载中...' : '刷新列表' }}
            </b-button>
          </template>

          <!-- PC端表格视图 -->
          <div class="d-none d-lg-block">
            <b-table
              :items="userList"
              :fields="userFields"
              :busy="loadingList"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              small
              hover
              responsive
              no-local-sorting
              @sort-changed="handleSort"
            >
              <template #table-busy>
                <div class="text-center text-primary my-2">
                  <b-spinner class="align-middle" small></b-spinner>
                  <strong class="ml-2">加载中...</strong>
                </div>
              </template>

              <template #cell(wx_open_id)="row">
                <code class="small text-muted">{{ row.item.wx_open_id || '-' }}</code>
              </template>

              <template #cell(roles)="row">
                <b-badge :variant="row.item.roles && row.item.roles.toLowerCase().includes('admin') ? 'danger' : 'info'">
                  {{ row.item.roles }}
                </b-badge>
              </template>

              <template #cell(status)="row">
                <b-badge :variant="row.item.status === 1 ? 'success' : 'secondary'">
                  {{ row.item.status === 1 ? '正常' : '禁用' }}
                </b-badge>
              </template>

              <template #cell(referral_points)="row">
                <span class="font-weight-bold text-warning">{{ row.item.referral_points }}</span>
              </template>

              <template #cell(create_time)="row">
                <span class="small">{{ row.item.create_time ? row.item.create_time.replace('T', ' ').substring(0, 19) : '-' }}</span>
              </template>

              <template #cell(last_login_time)="row">
                <span class="small">{{ row.item.last_login_time ? row.item.last_login_time.replace('T', ' ').substring(0, 19) : '-' }}</span>
              </template>

              <template #cell(points)="row">
                <span class="font-weight-bold text-success">{{ row.item.points }}</span>
              </template>

              <template #cell(actions)="row">
                <b-button size="sm" variant="outline-primary" class="py-0 px-2" @click="viewUserPermissions(row.item)">
                  查看权限
                </b-button>
              </template>
            </b-table>
          </div>

          <!-- 移动端卡片视图 -->
          <div class="d-lg-none user-mobile-list">
            <div v-if="loadingList" class="text-center text-primary my-4">
              <b-spinner class="align-middle" small></b-spinner>
              <strong class="ml-2">加载中...</strong>
            </div>
            <template v-else>
              <div v-for="user in userList" :key="user.user_id" class="user-mobile-card mb-2 p-2 bg-dark rounded border border-secondary shadow-sm">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="d-flex align-items-center flex-wrap">
                    <span class="font-weight-bold text-white mr-1" style="font-size: 0.9rem;">#{{ user.user_id }}</span>
                    <span class="text-primary font-weight-bold mr-2" style="font-size: 0.85rem;">{{ user.user_nick || '-' }}</span>
                    <b-badge :variant="user.roles && user.roles.toLowerCase().includes('admin') ? 'danger' : 'info'" class="mr-1 py-0 px-1" style="font-size: 0.7rem;">{{ user.roles }}</b-badge>
                    <b-badge :variant="user.status === 1 ? 'success' : 'secondary'" class="py-0 px-1" style="font-size: 0.7rem;">{{ user.status === 1 ? '正常' : '禁用' }}</b-badge>
                  </div>
                  <b-button size="sm" variant="outline-primary" class="py-0 px-2" style="font-size: 0.75rem;" @click="viewUserPermissions(user)">查看权限</b-button>
                </div>
                
                <div class="d-flex justify-content-between text-muted mb-1 flex-wrap" style="font-size: 0.8rem;">
                  <div>
                    <span class="mr-2">💰 <span class="text-success font-weight-bold">{{ user.points }}</span></span>
                    <span>🎁 <span class="text-warning font-weight-bold">{{ user.referral_points }}</span></span>
                  </div>
                  <div class="text-white-50">{{ user.email || '-' }}</div>
                </div>

                <div v-if="user.wx_open_id" class="mb-1" style="font-size: 0.75rem;">
                  <span class="text-muted mr-1">微信:</span>
                  <code class="text-muted">{{ user.wx_open_id }}</code>
                </div>
                
                <div class="d-flex justify-content-between text-muted flex-wrap" style="font-size: 0.75rem;">
                  <div>创: {{ user.create_time ? user.create_time.replace('T', ' ').substring(5, 16) : '-' }}</div>
                  <div>登: {{ user.last_login_time ? user.last_login_time.replace('T', ' ').substring(5, 16) : '-' }}</div>
                </div>
              </div>
              <div v-if="userList.length === 0" class="text-center py-4 text-muted bg-dark rounded border border-secondary">暂无用户数据</div>
            </template>
          </div>

          <!-- 分页 -->
          <div class="admin-pagination-row mt-3" v-if="totalUsers > 0">
            <span class="text-muted small">共 {{ totalUsers }} 个用户</span>
            <b-pagination
              v-model="page"
              :total-rows="totalUsers"
              :per-page="50"
              size="sm"
              class="my-0"
              @change="handlePageChange"
            />
          </div>
        </b-card>
      </b-tab>

      <!-- ═══════════════════════════════════════════════════════════════
           Tab 2: 规则配置
      ═══════════════════════════════════════════════════════════════ -->
      <b-tab title="规则配置">
        <!-- 积分档位规则 -->
        <b-card class="mb-4 shadow-sm">
          <template #header>
            <span class="font-weight-bold">📊 积分档位规则</span>
            <b-button size="sm" variant="success" class="float-right" @click="openPointsRateModal(null)">＋ 新增</b-button>
          </template>
          <!-- PC端表格视图 -->
          <div class="d-none d-lg-block">
            <b-table
              :items="pointsRateRules"
              :fields="pointsRateFields"
              small
              hover
              responsive
              show-empty
              empty-text="暂无规则"
            >
              <template #cell(endpoint)="row">
                <div class="d-flex align-items-center">
                  <code class="text-primary font-weight-bold mr-2" style="font-size: 0.95rem;">
                    {{ getFriendlyEndpointName(row.item.endpoint) }}
                  </code>
                  <b-badge v-if="row.item.endpoint.endsWith('_ip')" variant="warning" class="px-2 py-1">IP 限制</b-badge>
                  <b-badge v-else variant="success" class="px-2 py-1">Token 限制</b-badge>
                  <span class="small text-muted ml-2">({{ row.item.endpoint }})</span>
                </div>
              </template>
              <template #cell(actions)="row">
                <b-button-group size="sm">
                  <b-button variant="outline-primary" @click="openPointsRateModal(row.item)">编辑</b-button>
                  <b-button variant="outline-danger" @click="deletePointsRate(row.item.id)">删除</b-button>
                </b-button-group>
              </template>
            </b-table>
          </div>

          <!-- 移动端卡片视图 -->
          <div class="d-lg-none points-rate-mobile-list">
            <div v-for="row in pointsRateRules" :key="row.id" class="points-rate-mobile-card mb-2 p-2 bg-dark rounded border border-secondary shadow-sm">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <div>
                  <code class="font-weight-bold text-primary mr-1" style="font-size: 0.85rem;">{{ getFriendlyEndpointName(row.endpoint) }}</code>
                  <b-badge v-if="row.endpoint.endsWith('_ip')" variant="warning" class="py-0 px-1" style="font-size: 0.65rem;">IP</b-badge>
                  <b-badge v-else variant="success" class="py-0 px-1" style="font-size: 0.65rem;">Token</b-badge>
                </div>
                <b-button-group size="sm">
                  <b-button variant="outline-primary" class="py-0 px-2" style="font-size: 0.75rem;" @click="openPointsRateModal(row)">编辑</b-button>
                  <b-button variant="outline-danger" class="py-0 px-2" style="font-size: 0.75rem;" @click="deletePointsRate(row.id)">删除</b-button>
                </b-button-group>
              </div>
              <div class="d-flex justify-content-between text-muted small flex-wrap" style="font-size: 0.8rem;">
                <div>最低积分: <span class="text-warning font-weight-bold">{{ row.min_points }}</span></div>
                <div>限频: <span class="text-success font-weight-bold">{{ row.limit_per_min }}/min</span></div>
              </div>
              <div class="small text-white-50 mt-1" style="font-size: 0.75rem;">Key: <code>{{ row.endpoint }}</code></div>
              <div v-if="row.description" class="text-white-50 mt-1 pt-1 border-top border-secondary" style="font-size: 0.75rem;">
                {{ row.description }}
              </div>
            </div>
            <div v-if="pointsRateRules.length === 0" class="text-center py-3 text-muted bg-dark rounded border border-secondary">暂无规则</div>
          </div>
        </b-card>

        <!-- 套餐模板 -->
        <b-card class="shadow-sm">
          <template #header>
            <span class="font-weight-bold">🎁 订阅套餐模板</span>
            <b-button size="sm" variant="success" class="float-right" @click="openPlanModal(null)">＋ 新增</b-button>
          </template>
          <!-- PC端表格视图 -->
          <div class="d-none d-lg-block">
            <b-table
              :items="subscriptionPlans"
              :fields="planFields"
              small
              hover
              responsive
              show-empty
              empty-text="暂无套餐"
            >
              <template #cell(endpoint)="row">
                <code v-if="row.item.endpoint === '*'" class="text-danger font-weight-bold" style="font-size: 0.95rem;">* (全部接口)</code>
                <code v-else class="text-primary font-weight-bold" style="font-size: 0.95rem;">
                  {{ getFriendlyEndpointName(row.item.endpoint) }}
                </code>
                <span v-if="row.item.endpoint !== '*'" class="small text-muted ml-2">({{ row.item.endpoint }})</span>
              </template>
              <template #cell(status)="row">
                <b-badge :variant="row.item.status === 1 ? 'success' : 'secondary'">
                  {{ row.item.status === 1 ? '需订阅' : '免费' }}
                </b-badge>
              </template>
              <template #cell(actions)="row">
                <b-button size="sm" variant="outline-primary" @click="openPlanModal(row.item)">编辑</b-button>
              </template>
            </b-table>
          </div>

          <!-- 移动端卡片视图 -->
          <div class="d-lg-none plans-mobile-list">
            <div v-for="plan in subscriptionPlans" :key="plan.id" class="plan-mobile-card mb-2 p-2 bg-dark rounded border border-secondary shadow-sm">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-white font-weight-bold" style="font-size: 0.95rem;">{{ plan.name }}</span>
                <b-badge :variant="plan.status === 1 ? 'success' : 'secondary'" class="py-0 px-1" style="font-size: 0.7rem;">
                  {{ plan.status === 1 ? '需订阅' : '免费' }}
                </b-badge>
              </div>
              <div class="d-flex justify-content-between text-muted mb-1 flex-wrap" style="font-size: 0.8rem;">
                <div>接口: <code>{{ plan.endpoint === '*' ? '*' : getFriendlyEndpointName(plan.endpoint) }}</code></div>
                <div>限频: <span class="text-success font-weight-bold">{{ plan.limit_per_min }}/min</span></div>
              </div>
              <div class="d-flex justify-content-between text-muted small flex-wrap" style="font-size: 0.8rem;">
                <div>类型: <span class="text-info">{{ plan.plan_type === 'monthly' ? '月度' : '年度' }}</span></div>
                <div>价格: <span class="text-warning font-weight-bold">{{ plan.price !== null ? '¥' + plan.price : '-' }}</span></div>
              </div>
              <div v-if="plan.description" class="text-white-50 mt-1 pt-1 border-top border-secondary mb-1" style="font-size: 0.75rem;">
                {{ plan.description }}
              </div>
              <div class="d-flex justify-content-between align-items-center mt-1 pt-1 border-top border-secondary">
                <span class="small text-muted">Key: <code>{{ plan.endpoint }}</code></span>
                <b-button size="sm" variant="outline-primary" class="py-0 px-2" style="font-size: 0.75rem;" @click="openPlanModal(plan)">编辑</b-button>
              </div>
            </div>
            <div v-if="subscriptionPlans.length === 0" class="text-center py-3 text-muted bg-dark rounded border border-secondary">暂无套餐</div>
          </div>
        </b-card>
      </b-tab>
    </b-tabs>

    <!-- ── 弹窗：调整积分 ── -->
    <b-modal v-model="showPointsModal" :title="pointsAction === 'add' ? '➕ 发放积分' : '➖ 扣除积分'" @ok="submitPoints" ok-title="确认" cancel-title="取消">
      <b-form-group label="调整数量" description="请输入正整数">
        <b-form-input v-model.number="pointsAmount" type="number" min="1" />
      </b-form-group>
      <b-form-group label="备注（可选）">
        <b-form-input v-model="pointsRemark" placeholder="操作原因" />
      </b-form-group>
    </b-modal>

    <!-- ── 弹窗：分配订阅 ── -->
    <b-modal v-model="showGrantModal" title="📋 分配订阅套餐" @ok="submitGrant" ok-title="确认" cancel-title="取消">
      <b-form-group label="选择套餐">
        <b-form-select v-model="grantPlanId" :options="planOptions" />
      </b-form-group>
      <b-form-group label="开始日期">
        <b-form-input v-model="grantStartDate" type="date" />
      </b-form-group>
      <b-form-group label="结束日期">
        <b-form-input v-model="grantEndDate" type="date" />
      </b-form-group>
    </b-modal>

    <!-- ── 弹窗：积分规则编辑 ── -->
    <b-modal v-model="showPointsRateModal" :title="editingRule ? '编辑积分规则' : '新增积分规则'" @ok="submitPointsRate" ok-title="保存" cancel-title="取消">
      <b-form-group label="接口标识 (endpoint)" description="注意：未登录 IP 限制请输入以 _ip 结尾的键（如 daily_ip）">
        <b-form-input v-model="ruleForm.endpoint" placeholder="如 daily / daily_ip / plates_stocks_rank" />
        <div class="small text-muted mt-2 p-2 bg-dark rounded border border-secondary">
          <div class="font-weight-bold text-white mb-1"><b-icon icon="info-circle-fill" class="mr-1 text-info"></b-icon> 常用接口标识参考 (Key Mapping)：</div>
          <div class="row text-white-50">
            <div class="col-6"><code>daily</code> → daily() [日线]</div>
            <div class="col-6"><code>rt_k</code> → rt_k() [实时]</div>
            <div class="col-6"><code>plates_rank</code> → plates_rank()</div>
            <div class="col-6"><code>plates_stocks_rank</code> → market_plate_stocks()</div>
            <div class="col-6"><code>plate_popular_reason</code> → market_plate_popular_reason()</div>
            <div class="col-6"><code>plates_rank_days</code> → plates_rank_days() [区间排名]</div>
            <div class="col-6"><code>plates_rank_days_new</code> → plates_rank_days_new()</div>
            <div class="col-6"><code>stk_mins</code> → stk_mins() [分钟]</div>
            <div class="col-6"><code>sentiment_timing</code> → sentiment_timing() [小周期择时]</div>
          </div>
        </div>
      </b-form-group>
      <b-form-group label="最低积分要求 (min_points)">
        <b-form-input v-model.number="ruleForm.min_points" type="number" />
      </b-form-group>
      <b-form-group label="限频次数/分钟 (limit_per_min)">
        <b-form-input v-model.number="ruleForm.limit_per_min" type="number" />
      </b-form-group>
      <b-form-group label="备注">
        <b-form-input v-model="ruleForm.description" />
      </b-form-group>
    </b-modal>

    <!-- ── 弹窗：套餐模板编辑 ── -->
    <b-modal v-model="showPlanModal" :title="editingPlan ? '编辑套餐' : '新增套餐'" @ok="submitPlan" ok-title="保存" cancel-title="取消">
      <b-form-group label="套餐名称"><b-form-input v-model="planForm.name" /></b-form-group>
      <b-form-group label="接口标识 (endpoint，* 表示全部)">
        <b-form-input v-model="planForm.endpoint" />
        <div class="small text-muted mt-2 p-2 bg-dark rounded border border-secondary">
          <div class="font-weight-bold text-white mb-1"><b-icon icon="info-circle-fill" class="mr-1 text-info"></b-icon> 常用接口标识参考 (Key Mapping)：</div>
          <div class="row text-white-50">
            <div class="col-6"><code>daily</code> → daily() [日线]</div>
            <div class="col-6"><code>rt_k</code> → rt_k() [实时]</div>
            <div class="col-6"><code>plates_rank</code> → plates_rank()</div>
            <div class="col-6"><code>plates_stocks_rank</code> → market_plate_stocks()</div>
            <div class="col-6"><code>plate_popular_reason</code> → market_plate_popular_reason()</div>
            <div class="col-6"><code>plates_rank_days</code> → plates_rank_days() [区间排名]</div>
            <div class="col-6"><code>plates_rank_days_new</code> → plates_rank_days_new()</div>
            <div class="col-6"><code>stk_mins</code> → stk_mins() [分钟]</div>
            <div class="col-6"><code>sentiment_timing</code> → sentiment_timing() [小周期择时]</div>
          </div>
        </div>
      </b-form-group>
      <b-form-group label="类型">
        <b-form-select v-model="planForm.plan_type" :options="[{value:'monthly',text:'月度'},{value:'yearly',text:'年度'}]" />
      </b-form-group>
      <b-form-group label="限频次数/分钟"><b-form-input v-model.number="planForm.limit_per_min" type="number" /></b-form-group>
      <b-form-group label="价格（元，可为空）"><b-form-input v-model.number="planForm.price" type="number" /></b-form-group>
      <b-form-group label="描述"><b-form-input v-model="planForm.description" /></b-form-group>
      <b-form-group label="状态">
        <b-form-select v-model="planForm.status" :options="[{value:1,text:'需订阅'},{value:0,text:'免费'}]" />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request'

const API = url => `/v3/admin${url}`

export default {
  name: 'AdminApiAuth',
  data() {
    return {
      activeTab: 0,

      // -- Tab1: 用户权益 --
      searchId: '',
      loadingUser: false,
      targetUser: null,
      userPoints: 0,
      userSubscriptions: [],
      subFields: [
        { key: 'plan_name', label: '套餐名称' },
        { key: 'endpoint', label: '接口' },
        { key: 'limit_per_min', label: '限频/min' },
        { key: 'start_date', label: '开始日期' },
        { key: 'end_date', label: '结束日期' },
      ],
      userList: [],
      loadingList: false,
      totalUsers: 0,
      page: 1,
      sortBy: 'user_id',
      sortDesc: true,
      userFields: [
        { key: 'user_id', label: 'ID', sortable: true },
        { key: 'user_nick', label: '昵称', sortable: false },
        { key: 'email', label: '邮箱', sortable: false },
        { key: 'wx_open_id', label: '微信 OpenID', sortable: false },
        { key: 'roles', label: '角色', sortable: true },
        { key: 'status', label: '状态', sortable: true },
        { key: 'points', label: '积分', sortable: true },
        { key: 'referral_points', label: '推荐积分', sortable: true },
        { key: 'create_time', label: '创建时间', sortable: true },
        { key: 'last_login_time', label: '最后登录', sortable: true },
        { key: 'actions', label: '操作' }
      ],

      // 积分弹窗
      showPointsModal: false,
      pointsAction: 'add',
      pointsAmount: 100,
      pointsRemark: '',

      // 分配订阅弹窗
      showGrantModal: false,
      grantPlanId: null,
      grantStartDate: '',
      grantEndDate: '',
      planOptions: [],

      // -- Tab2: 规则配置 --
      pointsRateRules: [],
      pointsRateFields: [
        { key: 'endpoint', label: '接口标识' },
        { key: 'min_points', label: '最低积分' },
        { key: 'limit_per_min', label: '限频/min' },
        { key: 'description', label: '备注' },
        { key: 'actions', label: '操作' },
      ],
      subscriptionPlans: [],
      planFields: [
        { key: 'name', label: '套餐名称' },
        { key: 'endpoint', label: '接口' },
        { key: 'plan_type', label: '类型' },
        { key: 'limit_per_min', label: '限频/min' },
        { key: 'price', label: '价格(元)' },
        { key: 'status', label: '状态' },
        { key: 'actions', label: '操作' },
      ],

      // 积分规则弹窗
      showPointsRateModal: false,
      editingRule: null,
      ruleForm: { endpoint: '', min_points: 0, limit_per_min: 60, description: '' },

      // 套餐弹窗
      showPlanModal: false,
      editingPlan: null,
      planForm: { name: '', endpoint: '', plan_type: 'monthly', limit_per_min: 120, price: null, description: '', status: 1 },
    }
  },
  created() {
    this.loadRules()
    this.loadPlans()
    this.fetchUserList()
  },
  methods: {
    getFriendlyEndpointName(endpoint) {
      if (!endpoint) return '';
      const isIp = endpoint.endsWith('_ip');
      const baseKey = isIp ? endpoint.slice(0, -3) : endpoint;
      const API_DEFINITIONS = {
        'daily': 'daily()',
        'plates_rank': 'plates_rank()',
        'plates_rank_days_new': 'plates_rank_days_new()',
        'plates_rank_days': 'plates_rank_days()',
        'plates_stocks_rank': 'market_plate_stocks()',
        'plate_popular_reason': 'market_plate_popular_reason()',
        'rt_k': 'rt_k()',
        'sentiment_timing': 'sentiment_timing()',
        'stk_mins': 'stk_mins()'
      };
      return API_DEFINITIONS[baseKey] || endpoint;
    },
    // ── 用户搜索 ──────────────────────────────────────────────────────
    async searchUser() {
      if (!this.searchId) return
      this.loadingUser = true
      this.targetUser = null
      try {
        const res = await request_zzquant({ url: API(`/user/${this.searchId}`), method: 'get' })
        const d = res.data && res.data.data ? res.data.data : null
        if (!d) throw new Error('用户不存在')
        this.targetUser = d.user
        this.userPoints = d.points
        this.userSubscriptions = d.subscriptions
      } catch (e) {
        this.$bvToast.toast(e.message || '查询失败', { variant: 'danger', solid: true, title: '错误' })
      } finally {
        this.loadingUser = false
      }
    },

    async fetchUserList() {
      this.loadingList = true
      try {
        const res = await request_zzquant({
          url: API('/users'),
          method: 'get',
          params: {
            page: this.page,
            page_size: 50,
            sort_by: this.sortBy,
            sort_dir: this.sortDesc ? 'desc' : 'asc'
          }
        })
        const data = (res.data && res.data.data) || {}
        this.userList = data.items || []
        this.totalUsers = data.total || 0
      } catch (e) {
        this.$bvToast.toast(e.message || '获取用户列表失败', { variant: 'danger', solid: true, title: '错误' })
      } finally {
        this.loadingList = false
      }
    },

    handleSort(ctx) {
      this.sortBy = ctx.sortBy || 'user_id'
      this.sortDesc = ctx.sortDesc || false
      this.page = 1
      this.fetchUserList()
    },

    handlePageChange(page) {
      this.page = page
      this.fetchUserList()
    },

    viewUserPermissions(item) {
      this.searchId = item.user_id.toString()
      this.searchUser()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    clearDetails() {
      this.targetUser = null
      this.searchId = ''
    },

    isExpiringSoon(endDate) {
      if (!endDate) return false
      const diff = (new Date(endDate) - new Date()) / 86400000
      return diff >= 0 && diff <= 7
    },

    // ── 积分操作 ──────────────────────────────────────────────────────
    openPointsModal(action) {
      this.pointsAction = action
      this.pointsAmount = 100
      this.pointsRemark = ''
      this.showPointsModal = true
    },
    async submitPoints(bvModalEvt) {
      bvModalEvt.preventDefault()
      try {
        await request_zzquant({
          url: API('/points/adjust'),
          method: 'post',
          data: {
            user_id: this.targetUser.user_id,
            amount: this.pointsAmount,
            action: this.pointsAction,
            remark: this.pointsRemark || undefined,
          }
        })
        this.$bvToast.toast('积分调整成功', { variant: 'success', solid: true, title: '成功' })
        this.showPointsModal = false
        this.searchUser()
      } catch (e) {
        this.$bvToast.toast(e.message || '操作失败', { variant: 'danger', solid: true, title: '错误' })
      }
    },

    // ── 分配订阅 ──────────────────────────────────────────────────────
    async openGrantModal() {
      await this.loadAllPlansForSelect()
      const today = new Date().toISOString().slice(0, 10)
      this.grantStartDate = today
      const nextMonth = new Date(); nextMonth.setMonth(nextMonth.getMonth() + 1)
      this.grantEndDate = nextMonth.toISOString().slice(0, 10)
      this.grantPlanId = this.planOptions.length ? this.planOptions[0].value : null
      this.showGrantModal = true
    },
    async loadAllPlansForSelect() {
      try {
        const res = await request_zzquant({ url: API('/plans'), method: 'get' })
        const plans = (res.data && res.data.data && res.data.data.plans) || []
        this.planOptions = plans.map(p => {
          const friendlyName = p.endpoint === '*' ? '* (全部接口)' : this.getFriendlyEndpointName(p.endpoint);
          return { value: p.id, text: `${p.name}（${friendlyName}，${p.limit_per_min}/min）` };
        })
      } catch (e) { /* ignore */ }
    },
    async submitGrant(bvModalEvt) {
      bvModalEvt.preventDefault()
      try {
        const res = await request_zzquant({
          url: API('/subscription/grant'),
          method: 'post',
          data: {
            user_id: this.targetUser.user_id,
            plan_id: this.grantPlanId,
            start_date: this.grantStartDate,
            end_date: this.grantEndDate,
          }
        })
        const msg = (res.data && res.data.message) || '分配成功'
        this.$bvToast.toast(msg, { variant: 'success', solid: true, title: '成功' })
        this.showGrantModal = false
        this.searchUser()
      } catch (e) {
        this.$bvToast.toast(e.message || '操作失败', { variant: 'danger', solid: true, title: '错误' })
      }
    },

    // ── 积分规则 CRUD ─────────────────────────────────────────────────
    async loadRules() {
      try {
        const res = await request_zzquant({ url: '/v3/admin/rules/points_rate', method: 'get' })
        this.pointsRateRules = (res.data && res.data.data && res.data.data.rules) || []
      } catch (e) { /* ignore */ }
    },
    openPointsRateModal(rule) {
      this.editingRule = rule
      this.ruleForm = rule
        ? { endpoint: rule.endpoint, min_points: rule.min_points, limit_per_min: rule.limit_per_min, description: rule.description || '' }
        : { endpoint: '', min_points: 0, limit_per_min: 60, description: '' }
      this.showPointsRateModal = true
    },
    async submitPointsRate(bvModalEvt) {
      bvModalEvt.preventDefault()
      try {
        if (this.editingRule) {
          await request_zzquant({ url: `/v3/admin/rules/points_rate/${this.editingRule.id}`, method: 'put', data: this.ruleForm })
        } else {
          await request_zzquant({ url: '/v3/admin/rules/points_rate', method: 'post', data: this.ruleForm })
        }
        this.$bvToast.toast('保存成功', { variant: 'success', solid: true, title: '成功' })
        this.showPointsRateModal = false
        this.loadRules()
      } catch (e) {
        this.$bvToast.toast(e.message || '保存失败', { variant: 'danger', solid: true, title: '错误' })
      }
    },
    async deletePointsRate(id) {
      const ok = await this.$bvModal.msgBoxConfirm('确定删除该规则吗？删除后将在5分钟内自动生效。', { title: '确认删除', okVariant: 'danger', okTitle: '删除', cancelTitle: '取消' })
      if (!ok) return
      try {
        await request_zzquant({ url: `/v3/admin/rules/points_rate/${id}`, method: 'delete' })
        this.$bvToast.toast('删除成功', { variant: 'success', solid: true, title: '成功' })
        this.loadRules()
      } catch (e) {
        this.$bvToast.toast(e.message || '删除失败', { variant: 'danger', solid: true, title: '错误' })
      }
    },

    // ── 套餐模板 CRUD ─────────────────────────────────────────────────
    async loadPlans() {
      try {
        const res = await request_zzquant({ url: '/v3/admin/rules/subscription_plan', method: 'get' })
        this.subscriptionPlans = (res.data && res.data.data && res.data.data.plans) || []
      } catch (e) { /* ignore */ }
    },
    openPlanModal(plan) {
      this.editingPlan = plan
      this.planForm = plan
        ? { name: plan.name, endpoint: plan.endpoint, plan_type: plan.plan_type, limit_per_min: plan.limit_per_min, price: plan.price, description: plan.description || '', status: plan.status }
        : { name: '', endpoint: '', plan_type: 'monthly', limit_per_min: 120, price: null, description: '', status: 1 }
      this.showPlanModal = true
    },
    async submitPlan(bvModalEvt) {
      bvModalEvt.preventDefault()
      try {
        if (this.editingPlan) {
          await request_zzquant({ url: `/v3/admin/rules/subscription_plan/${this.editingPlan.id}`, method: 'put', data: this.planForm })
        } else {
          await request_zzquant({ url: '/v3/admin/rules/subscription_plan', method: 'post', data: this.planForm })
        }
        this.$bvToast.toast('保存成功', { variant: 'success', solid: true, title: '成功' })
        this.showPlanModal = false
        this.loadPlans()
      } catch (e) {
        this.$bvToast.toast(e.message || '保存失败', { variant: 'danger', solid: true, title: '错误' })
      }
    },
  }
}
</script>

<style scoped>
.admin-tabs >>> .nav-pills .nav-link {
  color: var(--text-main);
}
.admin-tabs >>> .nav-pills .nav-link.active {
  background-color: var(--accent-color);
}

.admin-search-wrapper {
  display: flex;
  align-items: center;
}
.admin-search-wrapper .search-input {
  max-width: 260px;
}
.admin-search-wrapper .search-btn {
  margin-left: 8px;
  white-space: nowrap;
}

.admin-pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

/* 手机端及平板适配，对齐 991px 断点 */
@media (max-width: 991px) {
  .admin-tabs >>> .nav-pills {
    display: flex !important;
    justify-content: space-around !important;
    border-bottom: 1px solid #2d3d51;
    margin-bottom: 8px !important;
    padding-bottom: 4px;
  }
  .admin-tabs >>> .nav-pills .nav-link {
    padding: 6px 12px !important;
    font-size: 0.85rem !important;
    flex: 1;
    text-align: center;
    border-radius: 4px;
    margin: 0 4px;
  }
  
  .admin-search-card {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    margin-bottom: 8px !important;
  }
  .admin-search-card >>> .card-body {
    padding: 0 !important;
  }

  .admin-search-wrapper {
    width: 100%;
  }
  .admin-search-wrapper .search-input {
    max-width: none;
    flex: 1;
  }
  
  .admin-pagination-row {
    flex-direction: column;
    justify-content: center;
    text-align: center;
  }
}

.user-mobile-card,
.points-rate-mobile-card,
.plan-mobile-card {
  transition: transform 0.2s, box-shadow 0.2s;
}
.user-mobile-card:hover,
.points-rate-mobile-card:hover,
.plan-mobile-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4) !important;
}
</style>
